import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    MessageBarBody,
    MessageBarTitle,
    Skeleton,
    SkeletonItem,
} from '@fluentui/react-components';
import { Clock12Regular } from '@fluentui/react-icons';

import { AppState } from '../../redux/reducers';
import { DocumentationPage } from '../../models/Entities/HelpDocumentation/DocumentationPage';
import * as HelpDocumentationService from '../../services/HelpDocumentation.Service';
import * as ComponentFunctions from './HelpDocumentation.Functions';
import HelpDocumentationFeedbackComponent from './HelpDocumentationFeedback/HelpDocumentationFeedback.Component';
import { GuideDrawer } from '../../theme/components/GuideDrawer';
import { GuideMessageBar } from '../../theme/components/GuideMessageBar';

import './HelpDocumentation.Styles.css';

export interface HelpDocumentationDrawerProps {
    /** Código de enlace con el que la API resuelve qué página mostrar. */
    linkCode: string;
    isOpen: boolean;
    onDismiss: () => void;
    /** Encabezado mientras la página todavía no llegó con el suyo. */
    fallbackTitle?: string;
}

const HelpDocumentationDrawerComponent: React.FC<HelpDocumentationDrawerProps> = ({
    linkCode,
    isOpen,
    onDismiss,
    fallbackTitle,
}) => {
    const [t] = useTranslation();
    const token = useSelector(
        (state: AppState) => (state as any)?.oidc?.user?.access_token as string | undefined
    );

    const [page, setPage] = useState<DocumentationPage | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [failed, setFailed] = useState<boolean>(false);

    const contentRef = useRef<HTMLDivElement | null>(null);

    //#region Contenido

    /**
     * Ya tenemos esta página cargada. Abrir el panel de nuevo no la vuelve a pedir:
     * la ayuda no cambia entre una apertura y la siguiente, y cerrarla y reabrirla
     * es justo lo que hace quien la está consultando mientras opera.
     *
     * Un intento fallido no deja página, así que reabrir sí reintenta.
     */
    const isLoaded = page?.linkCode === linkCode;

    useEffect(() => {
        if (!isOpen || !linkCode || !token || isLoaded) return;

        let cancelled = false;

        setIsLoading(true);
        setFailed(false);

        HelpDocumentationService.GetDocumentationPage(linkCode, token)
            .then(result => {
                if (cancelled) return;
                setPage(result);
            })
            .catch(() => {
                if (cancelled) return;
                // El detalle del fallo no le dice nada a quien buscaba ayuda: lo que
                // necesita saber es que no se pudo traer y que puede reintentar.
                setFailed(true);
                setPage(null);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [isOpen, linkCode, token, isLoaded]);

    const content = page?.content;

    /**
     * El HTML llega saneado por la API —es la dueña de esa política, y la aplica
     * igual para todos los proveedores— así que se incrusta tal cual. Después hay
     * que retocar el DOM resultante por dos cosas que el HTML solo no puede
     * resolver: las imágenes y los enlaces.
     */
    useEffect(() => {
        const host = contentRef.current;
        if (!host || !content) return;

        // Un enlace de la ayuda que navegue en la misma pestaña se lleva puesta la
        // pantalla desde la que se pidió, con lo que el usuario estuviera haciendo.
        host.querySelectorAll('a[href]').forEach(anchor => {
            anchor.setAttribute('target', '_blank');
            anchor.setAttribute('rel', 'noopener noreferrer');
        });

        // La API marca los enlaces a video pero no arma el reproductor: deja que
        // decida el front. Acá se arma, que es lo que espera quien ve un video en
        // la documentación y no un enlace más.
        host.querySelectorAll('a.doc-embed[data-embed-id]').forEach(link => {
            const kind = link.getAttribute('data-embed');
            const id = link.getAttribute('data-embed-id');
            if (!id) return;

            // Hash de privacidad de un video no listado: Vimeo se niega a
            // reproducirlo sin él, y viene aparte porque no es parte del id.
            const hash = link.getAttribute('data-embed-hash');

            const frame = document.createElement('iframe');
            frame.src =
                kind === 'vimeo'
                    ? 'https://player.vimeo.com/video/' +
                      encodeURIComponent(id) +
                      (hash ? '?h=' + encodeURIComponent(hash) : '')
                    // El dominio sin cookies es el mismo reproductor sin el rastreo
                    // que YouTube deja por defecto.
                    : 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id);
            frame.title = link.textContent?.trim() || 'Video';
            // Por atributo: la definición de tipos de esta versión de TS no conoce
            // `loading` en el iframe, aunque el navegador sí.
            frame.setAttribute('loading', 'lazy');
            frame.allow = 'accelerometer; encrypted-media; picture-in-picture; fullscreen';
            frame.allowFullscreen = true;

            const wrapper = document.createElement('div');
            wrapper.className = 'help-doc__embed';
            wrapper.appendChild(frame);

            link.replaceWith(wrapper);
        });

        let cancelled = false;
        const objectUrls: string[] = [];

        // Las imágenes se bajan con el token y se le pasan al elemento como blob:
        // el endpoint las protege igual que al resto, y un `<img src>` no puede
        // mandar un encabezado de autorización.
        // Los enlaces y el reproductor no dependen del token; las imágenes sí.
        if (!token) {
            return () => {
                cancelled = true;
            };
        }

        host.querySelectorAll('img').forEach(image => {
            // La dirección original se guarda la primera vez. Al vaciar el src, un
            // segundo pase del efecto —el token se renueva sin que cambie el
            // contenido— no tendría de dónde sacarla, y como la limpieza revoca los
            // blobs, las imágenes quedaban vacías para siempre.
            const original = image.dataset.docMedia || image.getAttribute('src') || '';
            const reference = ComponentFunctions.readMediaReference(original);

            if (!reference) {
                // Sin dirección alguna: la API no pudo resolver el archivo y lo dice
                // marcando la imagen. Se marca igual acá porque un `img` sin `src` no
                // dibuja nada —ni el ícono de rota— y el lector vería un hueco mudo
                // donde el autor puso una captura; el marcador deja el `alt` a la
                // vista. Una imagen con dirección propia que no apunta a la API
                // (externa, `data:`) se deja como está: esa carga sola.
                if (!original) image.setAttribute('data-doc-media-failed', 'true');
                return;
            }

            image.dataset.docMedia = original;

            // Se vacía el src para que el navegador no pida ya mismo una dirección
            // que va a responder 401 y deje el ícono de imagen rota a la vista.
            image.removeAttribute('src');

            HelpDocumentationService.GetDocumentationMedia(reference.linkCode, reference.mediaId, token)
                .then(blob => {
                    if (cancelled) return;

                    const objectUrl = URL.createObjectURL(blob);
                    objectUrls.push(objectUrl);
                    image.src = objectUrl;
                })
                .catch(() => {
                    if (cancelled) return;

                    // Que una imagen no cargue no invalida la página —el texto sigue
                    // sirviendo— pero desaparecer en silencio sí engaña: sin `src` el
                    // navegador no dibuja ni el ícono de rota, así que el lector ve un
                    // hueco donde el autor puso una captura y no se entera. Se marca
                    // para dejar el alt a la vista en su lugar.
                    image.setAttribute('data-doc-media-failed', 'true');
                });
        });

        return () => {
            cancelled = true;
            objectUrls.forEach(url => URL.revokeObjectURL(url));
        };
        // `isOpen` va en las dependencias porque el panel desmonta su cuerpo al
        // cerrarse: al reabrir, React vuelve a escribir el HTML original y las
        // imágenes recuperan su `src` de origen, que responde 401. Sin esto el
        // efecto no volvía a correr —el contenido es el mismo texto— y quedaban
        // rotas a partir de la segunda apertura.
    }, [content, token, isOpen]);

    //#endregion Contenido

    /**
     * Mientras se busca, el encabezado dice que se está buscando. El título de
     * respaldo lo pone la pantalla que abrió la ayuda —"Facturables", por ejemplo—
     * y mostrarlo en la espera afirma algo que todavía no sabemos: que la página
     * se llama así. Recién sirve cuando la página llegó sin título propio, o
     * cuando no llegó.
     */
    const title = isLoading
        ? t('helpDocumentation.loading')
        : page?.title || fallbackTitle || t('helpDocumentation.title');
    const lastModified = ComponentFunctions.formatLastModified(page?.lastModified);
    const isEmpty = !isLoading && !failed && !!page && !ComponentFunctions.hasRenderableContent(content);

    return (
        /* Documento para leer al lado de la pantalla: drawer "large" de la guía. El
           contexto ("Ayuda") va arriba del título y la fecha de actualización debajo. */
        <GuideDrawer
            open={isOpen}
            size="large"
            eyebrow={t('helpDocumentation.title')}
            title={title}
            subtitle={
                lastModified ? (
                    <span className="help-doc__titleMeta">
                        <Clock12Regular aria-hidden="true" />
                        {t('helpDocumentation.lastModified', { date: lastModified })}
                    </span>
                ) : undefined
            }
            onDismiss={onDismiss}
            closeLabel={t('helpDocumentation.close')}
            testId="help-documentation-drawer"
        >
            {/* Esqueleto con la forma de una página de documentación —título,
                párrafo, imagen, otro bloque— en lugar de un spinner. Ocupa el
                lugar que va a ocupar el contenido, así la llegada no reacomoda
                todo de golpe, y dice qué se está esperando: un documento. */}
            {isLoading && (
                <div className="help-doc__skeleton" aria-busy="true" aria-live="polite">
                    <span className="help-doc__srOnly">{t('helpDocumentation.loading')}</span>
                    <Skeleton animation="wave">
                        <SkeletonItem className="help-doc__skTitle" />

                        {/* Los renglones no son todos del mismo largo: un párrafo
                            real termina a media línea, y una columna de barras
                            idénticas se lee como una tabla, no como texto. */}
                        <div className="help-doc__skPara">
                            <SkeletonItem size={12} />
                            <SkeletonItem size={12} style={{ width: '94%' }} />
                            <SkeletonItem size={12} style={{ width: '61%' }} />
                        </div>

                        <SkeletonItem className="help-doc__skHeading" />

                        <div className="help-doc__skPara">
                            <SkeletonItem size={12} />
                            <SkeletonItem size={12} style={{ width: '88%' }} />
                            <SkeletonItem size={12} style={{ width: '43%' }} />
                        </div>

                        <SkeletonItem className="help-doc__skImage" />

                        <SkeletonItem className="help-doc__skHeading" />

                        {/* Una lista: la viñeta es lo que la delata como lista y
                            no como otro párrafo. */}
                        <div className="help-doc__skList">
                            {['86%', '72%', '91%'].map(width => (
                                <div key={width} className="help-doc__skListItem">
                                    <SkeletonItem shape="circle" size={8} />
                                    <SkeletonItem size={12} style={{ width }} />
                                </div>
                            ))}
                        </div>

                        <div className="help-doc__skPara">
                            <SkeletonItem size={12} />
                            <SkeletonItem size={12} style={{ width: '67%' }} />
                        </div>
                    </Skeleton>
                </div>
            )}

            {failed && (
                <GuideMessageBar intent="error" layout="multiline">
                    <MessageBarBody>
                        <MessageBarTitle>{t('helpDocumentation.error.title')}</MessageBarTitle>
                        {t('helpDocumentation.error.description')}
                    </MessageBarBody>
                </GuideMessageBar>
            )}

            {isEmpty && (
                <GuideMessageBar intent="info" layout="multiline">
                    <MessageBarBody>
                        <MessageBarTitle>{t('helpDocumentation.empty.title')}</MessageBarTitle>
                        {t('helpDocumentation.empty.description')}
                    </MessageBarBody>
                </GuideMessageBar>
            )}

            {ComponentFunctions.hasRenderableContent(content) && (
                <div
                    ref={contentRef}
                    className="help-doc__content"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: content as string }}
                />
            )}

            {/* Al pie y sólo con la página cargada: se pregunta si sirvió
                después de haberla podido leer, no mientras carga ni sobre un
                error. */}
            {ComponentFunctions.hasRenderableContent(content) && (
                <HelpDocumentationFeedbackComponent linkCode={linkCode} />
            )}
        </GuideDrawer>
    );
};

export default HelpDocumentationDrawerComponent;
